import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Wallet, CreditCard } from "lucide-react";
import PayPalSubscribeButton from "../../components/PayPalSubscribeButton";
import { getPlan, getPayroll } from "../../data/plans";
import { PaymentCard } from './components/PaymentCard';
import { OrderSummary } from './components/OrderSummary';
import type { PaymentMethod, SubscribeSuccess } from './types';

export default function Billing(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const planSlug: string = params.get("plan") || "essentials";
  const billingPeriod: string = params.get("billing") || "monthly";
  const VALID_PAYROLL = ["core", "premium", "elite"] as const;
  const rawPayroll = params.get("payroll");
  const payrollSlug: string | null = rawPayroll && (VALID_PAYROLL as readonly string[]).includes(rawPayroll) ? rawPayroll : null;

  const combinedSlug: string = payrollSlug ? planSlug + "_payroll_" + payrollSlug : planSlug;

  const plan = getPlan(planSlug);
  const payroll = getPayroll(payrollSlug);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("paypal");
  const [error, setError] = useState<string>("");

  const planMonthly: number = plan.monthlyPrice;
  const payrollMonthly: number = payroll ? payroll.monthlyPrice : 0;
  const subtotal: number = planMonthly + payrollMonthly;
  const founderDiscount: number = subtotal * 0.4;
  const totalDueToday: number = subtotal - founderDiscount;

  const handleSuccess = async (sub: SubscribeSuccess): Promise<void> => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (token) {
      try {
        const API = process.env.REACT_APP_API_URL || "https://api.getnovala.com";
        const paypalSubId = (typeof sub === "string") ? sub : (sub.subscriptionID || sub.id);
        const response = await fetch(API + "/api/v1/subscriptions/activate", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          body: JSON.stringify({ subscription_id: paypalSubId, plan_slug: combinedSlug })
        });
        if (response.ok) { navigate("/dashboard"); return; }
        const detail = await response.text();
        setError(t("billing.activationFailed") + " (" + detail.slice(0, 200) + ")");
      } catch (err) {
        setError(t("billing.networkError"));
      }
      return;
    }
    const next = new URLSearchParams();
    next.set("fromCheckout", "true");
    next.set("plan", planSlug);
    if (billingPeriod) next.set("billing", billingPeriod);
    if (payrollSlug) next.set("payroll", payrollSlug);
    navigate("/register?" + next.toString());
  };

  const handleError = (err: unknown): void => {
    const msg = (err && typeof err === "object" && "message" in err) ? String((err as { message: unknown }).message) : String(err);
    setError(t("billing.paymentFailed") + ": " + msg);
  };

  return (
    <div className="min-h-screen font-sans" style={{ background: "#FAFBFC", padding: "28px 32px 90px", maxWidth: "100%", margin: 0 }}>
      <div className="mx-auto" style={{ maxWidth: 1100 }}>

        <button type="button" onClick={() => navigate("/cart")} className="mb-5 inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border-none bg-ink px-4 py-2.5 text-small font-semibold text-white shadow-button">
          <ArrowLeft size={14} /> {t("billing.backToCart")}
        </button>

        <div className="grid items-start gap-6" style={{ gridTemplateColumns: "1fr 320px" }}>

          <div className="rounded-md border border-hairline bg-white" style={{ padding: "28px 26px" }}>
            <div className="mb-1.5 text-heading font-semibold text-ink">{t("billing.title")}</div>
            <div className="mb-6 text-small text-sub leading-relaxed">{t("billing.subtitle")}</div>

            <div className="mb-2.5 text-small font-medium text-ink">{t("billing.paymentMethod")}</div>
            <div className="mb-3 grid grid-cols-2 gap-2.5">
              <PaymentCard active={paymentMethod === "paypal"} title={t("billing.paypalTitle")} subtitle={t("billing.paypalSubtitle")} icon={<Wallet size={18} className="text-ink" strokeWidth={1.8} />} onClick={() => setPaymentMethod("paypal")} />
              <PaymentCard active={paymentMethod === "card"} title={t("billing.cardTitle")} subtitle={t("billing.cardSubtitle")} icon={<CreditCard size={18} className="text-ink" strokeWidth={1.8} />} onClick={() => setPaymentMethod("card")} />
            </div>

            <div className="mb-5 text-tiny text-sub leading-relaxed">
              {paymentMethod === "paypal" ? t("billing.paypalHelper") : t("billing.cardHelper")}
            </div>

            {error && (
              <div className="mb-4 rounded-md border p-3 text-small" style={{ background: "#FCE9E7", borderColor: "#F2C1BC", color: "#D9453C" }}>{error}</div>
            )}

            <div className="novala-paypal-slot rounded-md border border-hairline p-3" style={{ background: "#F9FAFB" }}>
              {paymentMethod === "paypal" && (
                <PayPalSubscribeButton planSlug={combinedSlug} fundingSource="paypal" onSuccess={handleSuccess} onError={handleError} />
              )}
              {paymentMethod === "card" && (
                <PayPalSubscribeButton planSlug={combinedSlug} fundingSource="card" onSuccess={handleSuccess} onError={handleError} />
              )}
            </div>

            <div className="mt-4 text-tiny text-sub" style={{ lineHeight: 1.6 }}>
              {t("billing.termsPrefix")}{" "}
              <a href="/terms" className="font-medium text-brand underline">{t("billing.termsLink")}</a>
              {" "}{t("billing.and")}{" "}
              <a href="/privacy" className="font-medium text-brand underline">{t("billing.privacyLink")}</a>.
              {" "}{t("billing.autoRenewNote")}
            </div>
          </div>

          <OrderSummary planName={plan.name} payrollName={payroll ? payroll.name : null} planMonthly={planMonthly} payrollMonthly={payrollMonthly} subtotal={subtotal} founderDiscount={founderDiscount} total={totalDueToday} t={t} />
        </div>
      </div>
    </div>
  );
}

